/**
 * Confirming a payment attempt in the browser, with the provider's own script.
 *
 * **Every value this needs is the attempt's.** The invoicing service answers a payment attempt
 * with the client secret, the publishable key of the account the intent was created through and,
 * for a direct charge on a seller's account, that account (its L18,
 * `docs/plans/billing-ledger.md` B7f). Nothing here is configured per deployment: the key is not
 * this product's, and reading it from the attempt is what keeps a browser from confirming an intent
 * against the wrong account or the wrong mode.
 *
 * **The script is Stripe's, loaded from `https://js.stripe.com` when a payment is opened and not
 * before.** Stripe requires it to be served from there rather than bundled, and the `pure` entry of
 * `@stripe/stripe-js` injects it on the first `loadStripe` call — where the package's default entry
 * injects it the moment the module is imported, which would put a third party's script on every
 * page this chunk is reachable from.
 *
 * **What this reports is what the provider said, and never that credits arrived.** A payment is
 * paid on this side when its top-up reads `completed`, which only the invoicing service's
 * `sale.paid` — drained by the worker — writes. The caller waits on that.
 */
import { onBeforeUnmount, ref } from 'vue'
import { loadStripe } from '@stripe/stripe-js/pure'
import type {
  Appearance,
  PaymentIntent,
  Stripe,
  StripeElements,
  StripeElementLocale,
  StripeError,
  StripePaymentElement,
} from '@stripe/stripe-js'

/** What an attempt hands the page. */
export interface PaymentSession {
  clientSecret: string
  publishableKey: string
  /** `null` on the platform's own account, which is where every credit sale is collected. */
  stripeAccount: string | null
}

/** How the element is drawn: the reader's language and the application's theme. */
export interface PaymentLook {
  locale: string
  dark: boolean
  /** The theme's primary colour, as `#rrggbb`. */
  primary?: string
}

/**
 * What confirming came to.
 *
 * - `succeeded` — the provider collected it; the credits follow the invoicing service's event.
 * - `processing` — the provider accepted it and is still collecting (a direct debit is days).
 * - `requires_action` — the bank still wants the payer, and the dialog was left.
 * - `declined` — this attempt is over: another one needs a **new** attempt, because a declined
 *   attempt is terminal at the invoicing service and a new one cancels it at the provider.
 * - `invalid` — the form is not complete. Nothing was sent; the element says what is missing.
 */
export type ConfirmOutcome =
  | { kind: 'succeeded' }
  | { kind: 'processing' }
  | { kind: 'requires_action' }
  | { kind: 'declined'; message: string }
  | { kind: 'invalid'; message: string }

/** The languages the element is offered in; anything else is left to the browser. */
function elementLocale(locale: string): StripeElementLocale {
  const primary = locale.toLowerCase().split(/[-_]/)[0]
  return primary === 'fr' || primary === 'en' ? primary : 'auto'
}

function outcomeOfIntent(intent: PaymentIntent): ConfirmOutcome {
  switch (intent.status) {
    case 'succeeded':
    case 'requires_capture':
      return { kind: 'succeeded' }
    case 'processing':
      return { kind: 'processing' }
    case 'requires_action':
      return { kind: 'requires_action' }
    case 'requires_payment_method':
      return {
        kind: 'declined',
        message: intent.last_payment_error?.message ?? '',
      }
    default:
      // `canceled`, or a word this build was not taught: the attempt is not payable any more.
      return { kind: 'declined', message: '' }
  }
}

function outcomeOfError(error: StripeError): ConfirmOutcome {
  // A form that is not complete is the payer's to fix, on this same attempt.
  if (error.type === 'validation_error') {
    return { kind: 'invalid', message: error.message ?? '' }
  }
  // Everything else ends this attempt: a card refused, an intent the invoicing service already
  // superseded or cancelled, the provider unreachable. Another try is another attempt.
  if (error.payment_intent) {
    const fromIntent = outcomeOfIntent(error.payment_intent)
    if (fromIntent.kind !== 'declined') return fromIntent
  }
  return { kind: 'declined', message: error.message ?? '' }
}

export function useStripePayment() {
  let stripe: Stripe | null = null
  let elements: StripeElements | null = null
  let element: StripePaymentElement | null = null

  /** Whether the element is mounted and has drawn itself. */
  const ready = ref(false)
  /**
   * The provider's name for the method the payer has picked in the element — `card`,
   * `sepa_debit`, … — or `null` before the element says. What the page reads to warn that a direct
   * debit takes days before it credits anything.
   */
  const method = ref<string | null>(null)

  function unmount(): void {
    element?.destroy()
    element = null
    elements = null
    stripe = null
    ready.value = false
    method.value = null
  }

  /**
   * Load the provider's script for this attempt's account, and mount the payment element on its
   * secret. Mounting again — a new attempt after a decline — replaces what was there.
   *
   * @throws when the script cannot be loaded (a blocker, the network) — the caller says so.
   */
  async function mount(session: PaymentSession, target: HTMLElement, look: PaymentLook): Promise<void> {
    unmount()
    const loaded = await loadStripe(
      session.publishableKey,
      session.stripeAccount ? { stripeAccount: session.stripeAccount } : undefined,
    )
    if (!loaded) {
      throw new Error('stripe.js did not load')
    }
    stripe = loaded

    const appearance: Appearance = {
      theme: look.dark ? 'night' : 'stripe',
      ...(look.primary ? { variables: { colorPrimary: look.primary } } : {}),
    }
    elements = stripe.elements({
      clientSecret: session.clientSecret,
      appearance,
      locale: elementLocale(look.locale),
    })
    element = elements.create('payment', { layout: 'tabs' })
    element.on('ready', () => {
      ready.value = true
    })
    element.on('change', (event) => {
      method.value = event.value?.type ?? null
    })
    element.mount(target)
  }

  /**
   * Confirm the mounted attempt.
   *
   * **`redirect: 'if_required'`**: a card's 3-D Secure is the provider's own dialog over this page,
   * and only a method that has to leave it (a bank's page) goes to `returnUrl`, which is where the
   * page picks the top-up up again.
   */
  async function confirm(returnUrl: string): Promise<ConfirmOutcome> {
    if (!stripe || !elements) {
      return { kind: 'invalid', message: '' }
    }
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    })
    if (result.error) return outcomeOfError(result.error)
    return outcomeOfIntent(result.paymentIntent)
  }

  onBeforeUnmount(unmount)

  return { ready, method, mount, confirm, unmount }
}
